import { useEffect, useState } from 'react'
import { Ban, Flag, MoreVertical, UserMinus, UserPlus } from 'lucide-react'
import type { CloudShoppingList, Language } from '../../models'
import {
  blockUser,
  cancelListInvite,
  inviteToListManaged,
  leaveListManaged,
  listBlockedUsers,
  loadListAccess,
  removeListMember,
  roleLabel,
  submitContentReport,
  subscribeListAccess,
  unblockUser,
  type BlockedUser,
  type ListAccess,
  type ListMember,
  type ReportTarget,
} from '../services/parityBackend'
import { copy, errorMessage, Field, FilledButton, Modal, Notice, OutlinedButton, TextButton } from './ParityUi'

const REPORT_REASONS = [
  ['spam', 'Spam', 'Spam'],
  ['abusive', 'Offensive or abusive content', 'Stötande eller kränkande innehåll'],
  ['sexual', 'Sexual/inappropriate content', 'Sexuellt/olämpligt innehåll'],
  ['violent', 'Violent/inappropriate content', 'Våldsamt/olämpligt innehåll'],
  ['misleading', 'Misleading or incorrect item', 'Vilseledande eller felaktig vara'],
  ['other', 'Other', 'Annat'],
] as const

export function ReportDialog({
  language,
  target,
  onClose,
  onSubmitted,
}: {
  language: Language
  target: ReportTarget
  onClose: () => void
  onSubmitted?: () => void
}) {
  const [reason, setReason] = useState<string | null>(null)
  const [details, setDetails] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const submit = async () => {
    if (!reason) return
    setBusy(true)
    setError('')
    try {
      await submitContentReport(target, reason, details)
      onSubmitted?.()
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  return <Modal title={copy(language, 'Report', 'Rapportera')} onClose={() => { if (!busy) onClose() }}>
    <div className="access-dialog-stack">
      <p>{target.label}</p>
      {error && <Notice error>{error}</Notice>}
      <div className="report-reasons">
        {REPORT_REASONS.map(([id, en, sv]) => <button key={id} type="button" className={`filter-chip ${reason === id ? 'selected' : ''}`} disabled={busy} onClick={() => setReason(id)}>{language === 'sv' ? sv : en}</button>)}
      </div>
      <Field label={copy(language, 'Optional details', 'Valfria detaljer')}><textarea rows={3} maxLength={500} disabled={busy} value={details} onChange={(event) => setDetails(event.target.value.slice(0, 500))} /></Field>
      <div className="dialog-actions">
        <TextButton disabled={busy} onClick={onClose}>{copy(language, 'Cancel', 'Avbryt')}</TextButton>
        <TextButton disabled={!reason || busy} onClick={() => void submit()}>{busy ? copy(language, 'Submitting…', 'Skickar…') : copy(language, 'Submit report', 'Skicka rapport')}</TextButton>
      </div>
    </div>
  </Modal>
}

function AccessRow({
  primary,
  secondary,
  actions,
}: {
  primary: string
  secondary: string
  actions?: Array<{ label: string; danger?: boolean; icon?: React.ReactNode; onClick: () => void }>
}) {
  const [open, setOpen] = useState(false)
  return <div className="access-row">
    <div className="access-row-copy"><strong>{primary}</strong><small>{secondary}</small></div>
    {actions && actions.length > 0 && <div className="overflow-wrap">
      <button type="button" className="icon-action" aria-label="Actions" onClick={() => setOpen((value) => !value)}><MoreVertical /></button>
      {open && <div className="overflow-popover access-popover">{actions.map((action) => <button key={action.label} type="button" className={action.danger ? 'danger-text' : ''} onClick={() => { setOpen(false); action.onClick() }}>{action.icon}{action.label}</button>)}</div>}
    </div>}
  </div>
}

export default function ListAccessDialog({
  language,
  list,
  currentUid,
  onClose,
  onLeft,
  onRevoked,
}: {
  language: Language
  list: CloudShoppingList
  currentUid: string
  onClose: () => void
  onLeft: () => void
  onRevoked: () => void
}) {
  const [access, setAccess] = useState<ListAccess | null>(null)
  const [email, setEmail] = useState('')
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [removeMember, setRemoveMember] = useState<ListMember | null>(null)
  const [blockMember, setBlockMember] = useState<ListMember | null>(null)
  const [leaveConfirm, setLeaveConfirm] = useState(false)
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null)

  useEffect(() => {
    let active = true
    void loadListAccess(list.id).then((next) => {
      if (active) setAccess(next)
    }).catch((cause) => {
      if (!active) return
      const text = errorMessage(cause)
      setError(text)
      if (text.toLowerCase().includes('no longer have access')) onRevoked()
    })
    const unsubscribe = subscribeListAccess(list.id, (next) => {
      setAccess(next)
      setError('')
    }, (cause) => {
      const text = errorMessage(cause)
      setError(text)
      if (text.toLowerCase().includes('no longer have access')) onRevoked()
    })
    return () => { active = false; unsubscribe() }
  }, [list.id, onRevoked])

  useEffect(() => {
    if (!access?.isOwner) {
      setBlockedUsers([])
      return
    }
    void listBlockedUsers().then(setBlockedUsers).catch(() => undefined)
  }, [access?.isOwner, access?.listId])

  const run = async (operation: () => Promise<void>, success: string) => {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      await operation()
      setMessage(success)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  const invite = async () => {
    const submitted = email.trim()
    if (!submitted) return
    await run(async () => {
      await inviteToListManaged(list.id, list.name, submitted)
      setEmail('')
    }, copy(language, 'Invitation sent.', 'Inbjudan skickad.'))
  }

  const ownerTarget = access && !access.isOwner && access.ownerUserId !== currentUid
    ? {
        targetType: 'collaborator', targetId: access.ownerUserId, targetOwnerUid: access.ownerUserId,
        source: 'shared_list_access', listId: list.id, label: access.ownerEmail ?? copy(language, 'List owner', 'Listägare'),
      }
    : null

  return <>
    <Modal title={copy(language, 'List access', 'Åtkomst till listan')} onClose={onClose} className="list-access-dialog">
      <div className="access-dialog-stack">
        {!access && !error && <p className="muted">{copy(language, 'Loading people and invitations…', 'Laddar personer och inbjudningar…')}</p>}
        {error && <Notice error>{error}</Notice>}
        {message && <Notice>{message}</Notice>}
        {access && <>
          <h3>{copy(language, 'People with access', 'Personer med åtkomst')}</h3>
          <AccessRow
            primary={access.ownerEmail ?? copy(language, 'List owner', 'Listägare')}
            secondary={copy(language, 'Owner', 'Ägare')}
            actions={ownerTarget ? [{ label: copy(language, 'Report user', 'Rapportera användare'), icon: <Flag />, onClick: () => setReportTarget(ownerTarget) }] : undefined}
          />
          {access.members.map((member) => {
            const isCurrent = member.userId === currentUid
            const report: ReportTarget = {
              targetType: 'collaborator', targetId: member.userId, targetOwnerUid: member.userId,
              source: 'shared_list_access', listId: list.id, label: member.email ?? copy(language, 'SHELFWAY user', 'SHELFWAY-användare'),
            }
            const actions = isCurrent ? [] : access.isOwner
              ? [
                  { label: copy(language, 'Remove from list', 'Ta bort från listan'), danger: true, icon: <UserMinus />, onClick: () => setRemoveMember(member) },
                  { label: copy(language, 'Block user', 'Blockera användare'), danger: true, icon: <Ban />, onClick: () => setBlockMember(member) },
                  { label: copy(language, 'Report user', 'Rapportera användare'), icon: <Flag />, onClick: () => setReportTarget(report) },
                ]
              : [{ label: copy(language, 'Report user', 'Rapportera användare'), icon: <Flag />, onClick: () => setReportTarget(report) }]
            return <AccessRow key={member.userId} primary={isCurrent ? copy(language, 'You', 'Du') : member.email ?? copy(language, 'SHELFWAY user', 'SHELFWAY-användare')} secondary={roleLabel(language, member.role)} actions={actions} />
          })}

          {access.isOwner ? <>
            <hr />
            <h3>{copy(language, 'Invite someone', 'Bjud in någon')}</h3>
            <Field label={copy(language, 'Email', 'E-post')}><input type="email" autoComplete="email" value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} /></Field>
            <FilledButton className="full" disabled={busy || !email.includes('@')} onClick={() => void invite()}><UserPlus />{busy ? copy(language, 'Sending invitation…', 'Skickar inbjudan…') : copy(language, 'Send invitation', 'Skicka inbjudan')}</FilledButton>

            {access.pendingInvites.length > 0 && <>
              <hr />
              <h3>{copy(language, 'Pending invitations', 'Väntande inbjudningar')}</h3>
              {access.pendingInvites.map((invite) => <AccessRow key={invite.id} primary={invite.recipientEmail} secondary={copy(language, 'Waiting for response', 'Väntar på svar')} actions={[{
                label: copy(language, 'Cancel invitation', 'Avbryt inbjudan'), danger: true,
                onClick: () => void run(() => cancelListInvite(invite.id), copy(language, 'Invitation cancelled.', 'Inbjudan avbruten.')),
              }]} />)}
            </>}

            {blockedUsers.length > 0 && <>
              <hr />
              <h3>{copy(language, 'Blocked users', 'Blockerade användare')}</h3>
              <p className="muted access-help">{copy(language, 'Blocked users cannot be invited to any of your lists.', 'Blockerade användare kan inte bjudas in till någon av dina listor.')}</p>
              {blockedUsers.map((blocked) => <AccessRow key={blocked.userId} primary={blocked.email ?? copy(language, 'SHELFWAY user', 'SHELFWAY-användare')} secondary={copy(language, 'Blocked', 'Blockerad')} actions={[{
                label: copy(language, 'Unblock', 'Avblockera'),
                onClick: () => void run(async () => {
                  await unblockUser(blocked.userId)
                  setBlockedUsers(await listBlockedUsers())
                }, copy(language, 'User unblocked.', 'Användaren avblockerades.')),
              }]} />)}
            </>}
          </> : <>
            <hr />
            <p className="muted access-help">{copy(language, 'Leaving removes this list from your account. The owner can invite you again later.', 'Om du lämnar tas listan bort från ditt konto. Ägaren kan bjuda in dig igen senare.')}</p>
            <TextButton className="danger-text" onClick={() => setLeaveConfirm(true)}>{copy(language, 'Leave list', 'Lämna listan')}</TextButton>
          </>}

          <hr />
          <TextButton onClick={() => setReportTarget({ targetType: 'shared_list_name', targetId: list.id, targetOwnerUid: access.ownerUserId, source: 'shared_list_access', listId: list.id, label: copy(language, 'Report this list name', 'Rapportera listnamnet') })}><Flag />{copy(language, 'Report list name', 'Rapportera listnamn')}</TextButton>
        </>}
        <div className="dialog-actions"><TextButton onClick={onClose}>{copy(language, 'Done', 'Klar')}</TextButton></div>
      </div>
    </Modal>

    {removeMember && <Modal title={copy(language, 'Remove access?', 'Ta bort åtkomst?')} onClose={() => setRemoveMember(null)}>
      <p>{copy(language, `${removeMember.email ?? 'This user'} will immediately lose access to this list. You can invite them again later.`, `${removeMember.email ?? 'Användaren'} förlorar omedelbart åtkomst till listan. Du kan bjuda in personen igen senare.`)}</p>
      <div className="dialog-actions"><TextButton onClick={() => setRemoveMember(null)}>{copy(language, 'Cancel', 'Avbryt')}</TextButton><TextButton className="danger-text" onClick={() => { const member = removeMember; setRemoveMember(null); void run(() => removeListMember(list.id, member.userId), copy(language, 'Access removed.', 'Åtkomst borttagen.')) }}>{copy(language, 'Remove', 'Ta bort')}</TextButton></div>
    </Modal>}

    {blockMember && <Modal title={copy(language, 'Block this user?', 'Blockera den här användaren?')} onClose={() => setBlockMember(null)}>
      <p>{copy(language, 'They will lose access to this list and cannot receive invitations to any of your lists until you unblock them from the sharing controls.', 'Personen förlorar åtkomsten till listan och kan inte få inbjudningar till någon av dina listor förrän du avblockerar personen i delningskontrollerna.')}</p>
      <div className="dialog-actions"><TextButton onClick={() => setBlockMember(null)}>{copy(language, 'Cancel', 'Avbryt')}</TextButton><TextButton className="danger-text" onClick={() => { const member = blockMember; setBlockMember(null); void run(async () => { await blockUser(member.userId, member.email, list.id); setBlockedUsers(await listBlockedUsers()) }, copy(language, 'User blocked and removed.', 'Användaren blockerades och togs bort.')) }}>{copy(language, 'Block user', 'Blockera användare')}</TextButton></div>
    </Modal>}

    {leaveConfirm && <Modal title={copy(language, 'Leave this list?', 'Lämna listan?')} onClose={() => setLeaveConfirm(false)}>
      <p>{copy(language, 'The list will be removed from your account. The owner can invite you again later.', 'Listan tas bort från ditt konto. Ägaren kan bjuda in dig igen senare.')}</p>
      <div className="dialog-actions"><TextButton onClick={() => setLeaveConfirm(false)}>{copy(language, 'Cancel', 'Avbryt')}</TextButton><TextButton className="danger-text" onClick={() => { setLeaveConfirm(false); void run(async () => { await leaveListManaged(list.id); onLeft() }, copy(language, 'You left the list.', 'Du lämnade listan.')) }}>{copy(language, 'Leave list', 'Lämna listan')}</TextButton></div>
    </Modal>}

    {reportTarget && <ReportDialog language={language} target={reportTarget} onClose={() => setReportTarget(null)} onSubmitted={() => setMessage(copy(language, 'Report submitted', 'Rapport skickad'))} />}
  </>
}
